import { ApiError, type ApiErrorBody } from './errors'

/**
 * The single place this application talks to the server.
 *
 * Every deployed environment serves the bundle and proxies the API from the
 * same origin, so requests are relative and the session cookie is sent by the
 * browser without any token handling here. That is the whole point: the portal
 * authenticates with a Secure, HttpOnly session cookie, so no credential is
 * ever readable by JavaScript, and there is nothing for an XSS to steal from
 * `localStorage`.
 *
 * Note what this is *not*: the client id and secret a customer creates here
 * are machine credentials for their own servers to present to `/api/v1/`. They
 * are shown once and never held by this application, which authenticates as a
 * person, not as an application.
 *
 * Because the cookie travels automatically, CSRF has to be defended
 * explicitly — hence the `X-CSRFToken` header on every unsafe method.
 */

export const API_ROOT = '/api/client/v1'

const CSRF_COOKIE = 'csrftoken'
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

/**
 * Ask the server to set the CSRF cookie.
 *
 * Django only issues `csrftoken` once something requests the token, and a
 * pure-JSON API never renders a form that would. Without this the first unsafe
 * request after a cold load has no token to echo and is rejected — which looks
 * like a broken login rather than a missing cookie.
 */
export async function primeCsrf(): Promise<void> {
  if (readCookie(CSRF_COOKIE)) return
  await fetch(`${API_ROOT}/auth/csrf/`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
}

export interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  query?: Record<string, string | number | undefined>
  /**
   * The caller's own id for this request, sent as `X-Request-ID`.
   *
   * The server records it beside its own request id and echoes it back, so a
   * customer can quote one value that means the same thing on both sides. It
   * is a correlation value only: it never becomes the request's identity.
   */
  correlationId?: string
}

function withQuery(path: string, query: RequestOptions['query']): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const search = params.toString()
  return search ? `${path}?${search}` : path
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }
  if (options.correlationId) {
    headers['X-Request-ID'] = options.correlationId
  }
  if (UNSAFE_METHODS.has(method)) {
    // The cookie must exist before the header can echo it.
    await primeCsrf()
    const csrf = readCookie(CSRF_COOKIE)
    if (csrf) {
      headers['X-CSRFToken'] = csrf
    }
  }

  const response = await fetch(`${API_ROOT}${withQuery(path, options.query)}`, {
    method,
    headers,
    // Same-origin rather than 'include': the API is never cross-origin in a
    // deployed environment, and 'include' would quietly permit one.
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      // A non-JSON body means something upstream answered instead of the API —
      // a proxy error page, most often. Fall through to the ApiError below so
      // the caller still gets a typed failure rather than a parse exception.
      payload = null
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, payload as ApiErrorBody | null, response.statusText, response.headers.get('Retry-After'))
  }

  return payload as T
}

/** The envelope every paginated list endpoint returns. */
export interface Page<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}
